import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Search, FileText, Zap, RefreshCw, Code2, Upload, Clock,
  Shield, Users, CreditCard, Palette, ArrowRight, ExternalLink,
  ChevronRight, Activity, MessageCircle,
} from 'lucide-react';
import { CATEGORIES, QUICK_TAGS } from '../help/helpCenterData.js';
import '../styles/docs.css';

const ICON_MAP = {
  'file-text': <FileText size={22} strokeWidth={1.5} />,
  zap: <Zap size={22} strokeWidth={1.5} />,
  'refresh-cw': <RefreshCw size={22} strokeWidth={1.5} />,
  'code-2': <Code2 size={22} strokeWidth={1.5} />,
  upload: <Upload size={22} strokeWidth={1.5} />,
  clock: <Clock size={22} strokeWidth={1.5} />,
  shield: <Shield size={22} strokeWidth={1.5} />,
  users: <Users size={22} strokeWidth={1.5} />,
  'credit-card': <CreditCard size={22} strokeWidth={1.5} />,
  palette: <Palette size={22} strokeWidth={1.5} />,
};

function CategoryCard({ cat, index }) {
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: index * 0.06 }}>
      <Link to={`/c/${cat.id}`} style={{ textDecoration: 'none' }}>
        <div className="hc-card" style={{ padding: '1.5rem', cursor: 'pointer', height: '100%' }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `${cat.color}18`,
              color: cat.color,
              marginBottom: '1rem',
              border: `1px solid ${cat.color}30`,
            }}
          >
            {ICON_MAP[cat.icon]}
          </div>

          <h3
            style={{
              color: '#F8FAFC',
              marginBottom: '0.5rem',
              fontSize: '1rem',
              fontWeight: 600,
              margin: '0 0 0.5rem 0',
              lineHeight: 1.4,
            }}
          >
            {cat.title}
          </h3>

          <p style={{ color: '#94A3B8', fontSize: '0.875rem', lineHeight: 1.6, margin: '0 0 1.25rem 0' }}>{cat.description}</p>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span
              style={{
                fontSize: '0.75rem',
                color: '#64748B',
                background: 'rgba(51,65,85,0.5)',
                padding: '3px 10px',
                borderRadius: 99,
                border: '1px solid #334155',
              }}
            >
              {cat.articles.length} artículos
            </span>
            <ArrowRight size={15} style={{ color: cat.color, opacity: 0.8 }} />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function searchArticles(query) {
  if (!query.trim()) return [];
  const q = query.toLowerCase();
  const results = [];
  for (const cat of CATEGORIES) {
    for (const art of cat.articles) {
      if (art.title.toLowerCase().includes(q) || cat.title.toLowerCase().includes(q) || art.content.toLowerCase().includes(q)) {
        results.push({ catId: cat.id, catTitle: cat.title, artId: art.id, artTitle: art.title });
        if (results.length >= 8) break;
      }
    }
    if (results.length >= 8) break;
  }
  return results;
}

const PARTICLES = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: Math.random() * 3 + 1,
  opacity: Math.random() * 0.4 + 0.1,
  duration: Math.random() * 8 + 6,
  delay: Math.random() * 4,
}));

export default function DocumentationPage() {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const results = searchArticles(query);
    setSearchResults(results);
    setShowResults(query.length > 1);
  }, [query]);

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      const first = searchResults[0];
      navigate(`/c/${first.catId}/${first.artId}`);
      setShowResults(false);
    }
    if (e.key === 'Escape') setShowResults(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0F172A' }}>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: 'rgba(15,23,42,0.85)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(51,65,85,0.6)',
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            padding: '0 1.5rem',
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', textDecoration: 'none' }}>
            <div
              style={{
                width: 30,
                height: 30,
                background: 'linear-gradient(135deg, #4F46E5, #22D3EE)',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FileText size={15} color="white" />
            </div>
            <span style={{ color: '#F8FAFC', fontWeight: 600, fontSize: '0.9375rem', letterSpacing: '-0.01em' }}>ReportEngine</span>
            <span style={{ color: '#64748B', fontSize: '0.875rem' }}>/ Help Center</span>
          </Link>
          <nav style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <Link
              to="/referencia-api"
              style={{
                color: '#94A3B8',
                fontSize: '0.875rem',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#F8FAFC')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#94A3B8')}
            >
              API Reference
              <ExternalLink size={12} />
            </Link>
            <a
              href="/estado-del-sistema"
              style={{
                color: '#94A3B8',
                fontSize: '0.875rem',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#22D3EE')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#94A3B8')}
            >
              <Activity size={13} />
              Estado del sistema
            </a>
          </nav>
        </div>
      </header>

      <section className="hc-hero-gradient" style={{ position: 'relative', overflow: 'hidden', padding: '5rem 1.5rem 6rem' }}>
        <div className="hc-grid-pattern" style={{ position: 'absolute', inset: 0, opacity: 0.5, pointerEvents: 'none' }} />

        {PARTICLES.map((p) => (
          <motion.div
            key={p.id}
            style={{
              position: 'absolute',
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: p.size,
              height: p.size,
              borderRadius: '50%',
              background: p.id % 3 === 0 ? '#4F46E5' : p.id % 3 === 1 ? '#22D3EE' : '#8B5CF6',
              opacity: p.opacity,
              pointerEvents: 'none',
            }}
            animate={{ y: [0, -20, 0], opacity: [p.opacity, p.opacity * 1.5, p.opacity] }}
            transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}

        <div
          style={{
            position: 'absolute',
            top: '-20%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 600,
            height: 400,
            background: 'radial-gradient(ellipse, rgba(79,70,229,0.2) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              background: 'rgba(79,70,229,0.15)',
              border: '1px solid rgba(79,70,229,0.35)',
              borderRadius: 99,
              padding: '5px 14px',
              marginBottom: '1.5rem',
              color: '#a5b4fc',
              fontSize: '0.8125rem',
              fontWeight: 500,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4F46E5', display: 'inline-block' }} />
            Documentación v2.0
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 700, color: '#F8FAFC', letterSpacing: '-0.03em', lineHeight: 1.15, marginBottom: '1rem' }}
          >
            ¿Cómo podemos{' '}
            <span
              style={{
                background: 'linear-gradient(90deg, #4F46E5, #22D3EE)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              ayudarte?
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.2 }}
            style={{ color: '#94A3B8', fontSize: '1.0625rem', lineHeight: 1.7, marginBottom: '2.5rem', maxWidth: 520, margin: '0 auto 2.5rem' }}
          >
            Documentación completa para diseñar, automatizar y escalar tus reportes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.3 }}
            ref={searchRef}
            style={{ position: 'relative', maxWidth: 580, margin: '0 auto' }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                background: 'rgba(30,41,59,0.9)',
                border: `1px solid ${showResults && query ? 'rgba(79,70,229,0.6)' : 'rgba(51,65,85,0.8)'}`,
                borderRadius: showResults && searchResults.length > 0 ? '12px 12px 0 0' : 12,
                padding: '0 1rem',
                gap: '0.75rem',
                backdropFilter: 'blur(8px)',
                boxShadow: showResults && query ? '0 0 0 3px rgba(79,70,229,0.15)' : '0 4px 24px rgba(0,0,0,0.3)',
                transition: 'all 0.2s',
              }}
            >
              <Search size={18} style={{ color: '#64748B', flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Buscar artículos, endpoints o conceptos…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => query.length > 1 && setShowResults(true)}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: '#F8FAFC',
                  fontSize: '0.9375rem',
                  padding: '0.875rem 0',
                  fontFamily: 'Inter, sans-serif',
                }}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    setShowResults(false);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#64748B',
                    cursor: 'pointer',
                    padding: '2px 6px',
                    borderRadius: 6,
                    fontSize: '0.8rem',
                    transition: 'color 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#F8FAFC')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#64748B')}
                >
                  ✕
                </button>
              )}
              <span
                style={{
                  flexShrink: 0,
                  background: 'rgba(51,65,85,0.6)',
                  border: '1px solid #334155',
                  borderRadius: 6,
                  padding: '2px 8px',
                  fontSize: '0.7rem',
                  color: '#64748B',
                  fontFamily: 'JetBrains Mono, monospace',
                }}
              >
                ⌘K
              </span>
            </div>

            {showResults && searchResults.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: '#1E293B',
                  border: '1px solid rgba(79,70,229,0.4)',
                  borderTop: 'none',
                  borderRadius: '0 0 12px 12px',
                  overflow: 'hidden',
                  boxShadow: '0 16px 40px rgba(0,0,0,0.4)',
                  zIndex: 100,
                }}
              >
                {searchResults.map((r, i) => (
                  <Link
                    key={i}
                    to={`/c/${r.catId}/${r.artId}`}
                    onClick={() => {
                      setShowResults(false);
                      setQuery('');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      textDecoration: 'none',
                      borderBottom: i < searchResults.length - 1 ? '1px solid rgba(51,65,85,0.4)' : 'none',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(79,70,229,0.08)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <Search size={13} style={{ color: '#64748B', flexShrink: 0 }} />
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{ color: '#F8FAFC', fontSize: '0.875rem', fontWeight: 500 }}>{r.artTitle}</div>
                      <div style={{ color: '#64748B', fontSize: '0.75rem' }}>{r.catTitle}</div>
                    </div>
                    <ChevronRight size={13} style={{ color: '#64748B' }} />
                  </Link>
                ))}
              </motion.div>
            )}

            {showResults && query.length > 1 && searchResults.length === 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: '#1E293B',
                  border: '1px solid rgba(79,70,229,0.4)',
                  borderTop: 'none',
                  borderRadius: '0 0 12px 12px',
                  padding: '1rem',
                  color: '#64748B',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  zIndex: 100,
                }}
              >
                No se encontraron resultados para "{query}"
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.5rem', marginTop: '1.25rem' }}
          >
            <span style={{ color: '#475569', fontSize: '0.8125rem', alignSelf: 'center' }}>Popular:</span>
            {QUICK_TAGS.map((tag) => (
              <Link key={tag.href} to={tag.href} className="hc-chip">
                {tag.label}
              </Link>
            ))}
          </motion.div>
        </div>
      </section>

      <div
        style={{
          background: 'rgba(30,41,59,0.5)',
          borderBottom: '1px solid rgba(51,65,85,0.5)',
          padding: '0.875rem 1.5rem',
        }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap' }}>
          {[
            { label: 'Categorías', value: CATEGORIES.length },
            { label: 'Artículos', value: CATEGORIES.reduce((a, c) => a + c.articles.length, 0) },
            { label: 'Actualizado', value: '21 Feb 2026' },
            { label: 'Versión', value: 'v2.0' },
          ].map((stat) => (
            <div key={stat.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: '#4F46E5', fontWeight: 700, fontSize: '0.9375rem', fontFamily: 'JetBrains Mono, monospace' }}>{stat.value}</span>
              <span style={{ color: '#64748B', fontSize: '0.8125rem' }}>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '3.5rem 1.5rem' }}>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} style={{ marginBottom: '2rem' }}>
          <h2 style={{ color: '#F8FAFC', fontSize: '1.25rem', fontWeight: 600, margin: '0 0 0.375rem', letterSpacing: '-0.02em' }}>Explorar documentación</h2>
          <p style={{ color: '#64748B', fontSize: '0.875rem', margin: 0 }}>Selecciona una categoría para ver todos los artículos disponibles.</p>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
          {CATEGORIES.map((cat, i) => (
            <CategoryCard key={cat.id} cat={cat} index={i} />
          ))}
        </div>
      </section>

      <footer>
        <div
          style={{
            background: 'rgba(30,41,59,0.6)',
            borderTop: '1px solid rgba(51,65,85,0.5)',
            padding: '3rem 1.5rem',
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: 480, margin: '0 auto' }}>
            <div
              style={{
                width: 48,
                height: 48,
                background: 'rgba(79,70,229,0.15)',
                border: '1px solid rgba(79,70,229,0.3)',
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.25rem',
                color: '#4F46E5',
              }}
            >
              <MessageCircle size={22} strokeWidth={1.5} />
            </div>
            <h3 style={{ color: '#F8FAFC', fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>¿No encontraste lo que buscabas?</h3>
            <p style={{ color: '#64748B', fontSize: '0.875rem', marginBottom: '1.5rem', lineHeight: 1.65 }}>
              Nuestro equipo está disponible para responder tus dudas y ayudarte a integrar la plataforma.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href="mailto:soporte@reportengine.app"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  padding: '0.625rem 1.25rem',
                  background: '#4F46E5',
                  color: 'white',
                  borderRadius: 10,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textDecoration: 'none',
                  transition: 'background 0.15s, transform 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#4338CA';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#4F46E5';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <MessageCircle size={14} />
                Contactar soporte
              </a>
            </div>
          </div>
        </div>

        <div style={{ background: '#0F172A', borderTop: '1px solid rgba(51,65,85,0.4)', padding: '1.25rem 1.5rem' }}>
          <div
            style={{
              maxWidth: 1200,
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '0.75rem',
            }}
          >
            <span style={{ color: '#334155', fontSize: '0.8125rem' }}>© 2026 ReportEngine</span>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              {[
                { label: 'API Reference', href: '/referencia-api', external: false },
                { label: 'Estado del sistema', href: '/estado-del-sistema', external: false },
                { label: 'Términos', href: '/terminos-de-servicio', external: false },
                { label: 'Privacidad', href: '/politica-de-privacidad', external: false },
              ].map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noreferrer' : undefined}
                  style={{ color: '#475569', fontSize: '0.8125rem', textDecoration: 'none', transition: 'color 0.15s' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#94A3B8')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#475569')}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
